/**
 * usePartDrawer Hook
 * POV-0042: Implement deep-link routing
 * POV-0044: Implement unsaved-changes protection
 * 
 * Custom hook for managing Part Overview Drawer state with URL synchronization.
 */
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

const STORAGE_KEY_PREFIX = 'part_drawer_state_';

/**
 * Hook to manage drawer state with deep-linking support
 */
export const usePartDrawer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get drawer state from URL params
  const panelPartId = searchParams.get('panel');
  const targetSection = searchParams.get('section');
  
  // Local state
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  // Check if drawer should be open
  const isOpen = !!panelPartId;

  /**
   * Open drawer for a specific part
   */
  const openDrawer = useCallback((partId, section = null) => {
    const params = new URLSearchParams(searchParams);
    params.set('panel', partId);
    if (section) {
      params.set('section', section);
    } else {
      params.delete('section');
    }
    setSearchParams(params, { replace: false });
    setIsDirty(false);
  }, [searchParams, setSearchParams]);

  /**
   * Close drawer (with unsaved changes check)
   */
  const closeDrawer = useCallback(() => {
    if (isDirty) {
      setShowConfirmModal(true);
      setPendingClose(true);
      return;
    }
    
    const params = new URLSearchParams(searchParams);
    params.delete('panel');
    params.delete('section');
    setSearchParams(params, { replace: false });
    setIsDirty(false);
  }, [isDirty, searchParams, setSearchParams]);

  /**
   * Force close drawer (discard changes)
   */
  const forceCloseDrawer = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('panel');
    params.delete('section');
    setSearchParams(params, { replace: false });
    setIsDirty(false);
    setShowConfirmModal(false);
    setPendingClose(false);
  }, [searchParams, setSearchParams]);

  /**
   * Cancel close and continue editing
   */
  const cancelClose = useCallback(() => {
    setShowConfirmModal(false);
    setPendingClose(false);
  }, []);

  /**
   * Navigate to a specific section within drawer
   */
  const navigateToSection = useCallback((sectionKey) => {
    if (!panelPartId) return;
    
    const params = new URLSearchParams(searchParams);
    params.set('section', sectionKey);
    setSearchParams(params, { replace: true });
  }, [panelPartId, searchParams, setSearchParams]);

  /**
   * Clear section from URL (after scroll complete)
   */
  const clearTargetSection = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('section');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Mark drawer as having unsaved changes
   */
  const markDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  /**
   * Clear dirty state
   */
  const clearDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  /**
   * Get saved expand/collapse state for a part
   */
  const getSavedExpandState = useCallback((partId) => {
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY_PREFIX}${partId}`);
      if (saved) {
        return JSON.parse(saved).expandedSections || ['overview'];
      }
    } catch (e) {
      console.warn('Failed to load saved drawer state:', e);
    }
    return ['overview'];
  }, []);

  /**
   * Save expand/collapse state for a part
   */
  const saveExpandState = useCallback((partId, expandedSections) => {
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${partId}`, JSON.stringify({
        expandedSections: Array.from(expandedSections),
        lastViewed: new Date().toISOString(),
      }));
    } catch (e) {
      console.warn('Failed to save drawer state:', e);
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // If navigating away with dirty state, show warning
      // Note: Browser handles this with beforeunload for page navigation
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Warn before page unload if dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return {
    // State
    isOpen,
    panelPartId,
    targetSection,
    isDirty,
    showConfirmModal,
    
    // Actions
    openDrawer,
    closeDrawer,
    forceCloseDrawer,
    cancelClose,
    navigateToSection,
    clearTargetSection,
    markDirty,
    clearDirty,
    
    // Persistence
    getSavedExpandState,
    saveExpandState,
  };
};

export default usePartDrawer;
